import L from "leaflet";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import type { EquipmentGpsTracking } from "../types";
import { formatDateTime, formatTime } from "../utils/time";

function createPinIcon(color: string) {
	return L.divIcon({
		className: "",
		html: `
			<svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
				<path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 24 14 24S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="2"/>
				<circle cx="14" cy="14" r="5" fill="white"/>
			</svg>`,
		iconSize: [28, 38],
		iconAnchor: [14, 38],
		popupAnchor: [0, -38],
	});
}

function createCarIcon(heading: number, speed: number) {
	const color =
		speed === 0 ? "#6b7280" : speed < 30 ? "#22c55e" : speed < 60 ? "#f59e0b" : "#ef4444";
	// Top-down car silhouette (nose up = heading 0deg) so it reads correctly on
	// a bird's-eye map, rather than the front-elevation Material glyph.
	return L.divIcon({
		className: "",
		html: `
			<div style="transform: rotate(${heading}deg); display:flex; align-items:center; justify-content:center;">
				<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
					<rect x="7" y="3" width="10" height="18" rx="3" fill="${color}" stroke="white" stroke-width="1"/>
					<rect x="5.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/>
					<rect x="17.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/>
					<rect x="8.5" y="6" width="7" height="4" rx="1.3" fill="white" fill-opacity="0.85"/>
					<rect x="8.5" y="15" width="7" height="2.8" rx="1" fill="white" fill-opacity="0.55"/>
				</svg>
			</div>`,
		iconSize: [32, 32],
		iconAnchor: [16, 16],
		popupAnchor: [0, -16],
	});
}

const startIcon = createPinIcon("#22c55e");
const endIcon = createPinIcon("#ef4444");

const DEFAULT_CENTER: [number, number] = [41.56, 60.62];
const DEFAULT_ZOOM = 13;

interface MapBoundsFitterProps {
	trackings: EquipmentGpsTracking[];
}

function MapBoundsFitter({ trackings }: MapBoundsFitterProps) {
	const map = useMap();

	useEffect(() => {
		if (trackings.length === 0) {
			return;
		}
		const points = trackings.map((t) => [t.latitude, t.longitude] as [number, number]);
		const bounds = L.latLngBounds(points);
		map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
	}, [map, trackings]);

	return null;
}

interface EquipmentGpsMapProps {
	trackings: EquipmentGpsTracking[];
}

const ANIMATION_INTERVAL_MS = 60;

export function EquipmentGpsMap({ trackings }: EquipmentGpsMapProps) {
	const [currentIdx, setCurrentIdx] = useState(() => Math.max(0, trackings.length - 1));
	const [isPlaying, setIsPlaying] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		setCurrentIdx(Math.max(0, trackings.length - 1));
		setIsPlaying(false);
	}, [trackings.length]);

	const positions = useMemo(
		() => trackings.map((t) => [t.latitude, t.longitude] as [number, number]),
		[trackings]
	);

	const hasTrackings = trackings.length > 0;
	const firstPoint = trackings[0];
	const lastPoint = trackings[trackings.length - 1];
	const currentPoint = trackings[currentIdx];

	const travelledPositions = useMemo(
		() => positions.slice(0, currentIdx + 1),
		[positions, currentIdx]
	);

	useEffect(() => {
		if (isPlaying) {
			intervalRef.current = setInterval(() => {
				setCurrentIdx((prev) => {
					if (prev >= trackings.length - 1) {
						setIsPlaying(false);
						return prev;
					}
					return prev + 1;
				});
			}, ANIMATION_INTERVAL_MS);
		} else if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isPlaying, trackings.length]);

	function handlePlayPause() {
		if (currentIdx >= trackings.length - 1) {
			setCurrentIdx(0);
			setIsPlaying(true);
		} else {
			setIsPlaying((p) => !p);
		}
	}

	function handleReset() {
		setIsPlaying(false);
		setCurrentIdx(0);
	}

	const progress = hasTrackings ? Math.round((currentIdx / (trackings.length - 1)) * 100) : 0;

	return (
		<div className="space-y-3">
			<div className="h-[420px] w-full rounded-lg overflow-hidden border border-neutral-200 relative">
				<MapContainer
					center={hasTrackings ? [firstPoint.latitude, firstPoint.longitude] : DEFAULT_CENTER}
					zoom={DEFAULT_ZOOM}
					className="h-full w-full"
					scrollWheelZoom
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{hasTrackings && <MapBoundsFitter trackings={trackings} />}

					{/* Full grey route */}
					{positions.length > 1 && (
						<Polyline
							positions={positions}
							pathOptions={{ color: "#d1d5db", weight: 4, opacity: 0.5 }}
						/>
					)}

					{/* Travelled route in blue */}
					{travelledPositions.length > 1 && (
						<Polyline
							positions={travelledPositions}
							pathOptions={{ color: "#1677ff", weight: 4 }}
						/>
					)}

					{/* Start marker */}
					{firstPoint && (
						<Marker position={[firstPoint.latitude, firstPoint.longitude]} icon={startIcon}>
							<Popup>
								<div className="text-sm min-w-[140px]">
									<strong style={{ color: "#22c55e" }}>Boshlanish</strong>
									<br />
									<span className="text-gray-500">{formatDateTime(firstPoint.receivedAt)}</span>
									<br />
									Tezlik: <strong>{firstPoint.speed} km/h</strong>
								</div>
							</Popup>
						</Marker>
					)}

					{/* End marker */}
					{lastPoint && lastPoint.id !== firstPoint?.id && (
						<Marker position={[lastPoint.latitude, lastPoint.longitude]} icon={endIcon}>
							<Popup>
								<div className="text-sm min-w-[140px]">
									<strong style={{ color: "#ef4444" }}>Tugash</strong>
									<br />
									<span className="text-gray-500">{formatDateTime(lastPoint.receivedAt)}</span>
									<br />
									Tezlik: <strong>{lastPoint.speed} km/h</strong>
								</div>
							</Popup>
						</Marker>
					)}

					{/* Animated car marker */}
					{currentPoint && (
						<Marker
							position={[currentPoint.latitude, currentPoint.longitude]}
							icon={createCarIcon(currentPoint.heading, currentPoint.speed)}
						>
							<Popup>
								<div className="text-sm min-w-[130px]">
									<strong>Joriy holat</strong>
									<br />
									<span className="text-gray-500">{formatDateTime(currentPoint.receivedAt)}</span>
									<br />
									Tezlik: <strong>{currentPoint.speed} km/h</strong>
									<br />
									Yo'nalish: {currentPoint.heading}°
								</div>
							</Popup>
						</Marker>
					)}
				</MapContainer>
			</div>

			{/* Playback controls */}
			{hasTrackings && (
				<div className="flex items-center gap-3 bg-gray-50 border border-neutral-200 rounded-xl px-4 py-2.5">
					{/* Reset */}
					<button
						type="button"
						onClick={handleReset}
						title="Qayta boshlash"
						className="text-ink-soft hover:text-ink transition-colors"
					>
						<svg
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="currentColor"
						>
							<path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
						</svg>
					</button>

					{/* Play / Pause */}
					<button
						type="button"
						onClick={handlePlayPause}
						title={isPlaying ? "Pauza" : "Ijro etish"}
						className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors shrink-0"
					>
						{isPlaying ? (
							<svg
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="currentColor"
							>
								<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
							</svg>
						) : (
							<svg
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="currentColor"
							>
								<path d="M8 5v14l11-7z" />
							</svg>
						)}
					</button>

					{/* Scrubber */}
					<div className="flex-1 flex flex-col gap-1 min-w-0">
						<input
							type="range"
							min={0}
							max={trackings.length - 1}
							value={currentIdx}
							onChange={(e) => {
								setIsPlaying(false);
								setCurrentIdx(Number(e.target.value));
							}}
							className="w-full h-1.5 accent-blue-600 cursor-pointer"
						/>
						<div className="flex justify-between text-[11px] text-ink-soft leading-none">
							<span>{formatTime(firstPoint?.receivedAt)}</span>
							<span className="text-ink-soft font-medium">{currentPoint?.speed ?? 0} km/h</span>
							<span>{formatTime(lastPoint?.receivedAt)}</span>
						</div>
					</div>

					{/* Progress badge */}
					<span className="text-xs font-semibold text-blue-600 w-9 text-right shrink-0">
						{progress}%
					</span>
				</div>
			)}
		</div>
	);
}
